import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Loader2, Sparkles, Save, Clock, FileText, Brain } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AISettingsPage = () => {
  const [settings, setSettings] = useState({
    ai_enabled: true,
    ai_model: 'gpt-4o',
    ai_temperature: 0.7,
    auto_summary_enabled: true,
    auto_summary_time: '17:00',
    auto_summary_timezone: 'America/Chicago',
    case_note_ai_enabled: true,
    case_note_ai_suggestions: true,
    case_note_ai_grammar_check: true,
    messenger_ai_summary_enabled: true,
    knowledge_base_enabled: false,
    max_tokens: 2000
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/ai-settings`);
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Failed to fetch AI settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/ai-settings`, settings);
      toast.success('AI settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ai-settings-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/settings">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Settings
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Sparkles className="w-6 h-6" />
              AI & Knowledge Center
            </h1>
            <p className="text-muted-foreground">Configure AI features and automatic summaries</p>
          </div>
        </div>
        <Button onClick={saveSettings} disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Settings
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* General AI Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Brain className="w-5 h-5" />
              General AI Settings
            </CardTitle>
            <CardDescription>Configure the AI model and parameters</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Enable AI Features</p>
                <p className="text-sm text-muted-foreground">Turn on AI-powered features across the system</p>
              </div>
              <Switch
                checked={settings.ai_enabled}
                onCheckedChange={(v) => setSettings({ ...settings, ai_enabled: v })}
              />
            </div>
            
            <div className="space-y-2">
              <Label>AI Model</Label>
              <Select
                value={settings.ai_model}
                onValueChange={(v) => setSettings({ ...settings, ai_model: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gpt-4o">GPT-4o (Recommended)</SelectItem>
                  <SelectItem value="gpt-4o-mini">GPT-4o Mini (Faster)</SelectItem>
                  <SelectItem value="gpt-3.5-turbo">GPT-3.5 Turbo (Economy)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Temperature (Creativity): {settings.ai_temperature}</Label>
              <Input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={settings.ai_temperature}
                onChange={(e) => setSettings({ ...settings, ai_temperature: parseFloat(e.target.value) })}
              />
              <p className="text-xs text-muted-foreground">Lower = more focused, Higher = more creative</p>
            </div>

            <div className="space-y-2">
              <Label>Max Tokens</Label>
              <Input
                type="number"
                value={settings.max_tokens}
                onChange={(e) => setSettings({ ...settings, max_tokens: parseInt(e.target.value) || 2000 })}
              />
              <p className="text-xs text-muted-foreground">Maximum length of AI responses</p>
            </div>
          </CardContent>
        </Card>

        {/* Auto Summary Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Automatic Summaries
            </CardTitle>
            <CardDescription>Configure daily SMS conversation summaries</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Daily SMS Summaries</p>
                <p className="text-sm text-muted-foreground">Automatically summarize SMS conversations daily</p>
              </div>
              <Switch
                checked={settings.auto_summary_enabled}
                onCheckedChange={(v) => setSettings({ ...settings, auto_summary_enabled: v })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Summary Time</Label>
                <Input
                  type="time"
                  value={settings.auto_summary_time}
                  onChange={(e) => setSettings({ ...settings, auto_summary_time: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Timezone</Label>
                <Select
                  value={settings.auto_summary_timezone}
                  onValueChange={(v) => setSettings({ ...settings, auto_summary_timezone: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="America/New_York">Eastern Time</SelectItem>
                    <SelectItem value="America/Chicago">Central Time</SelectItem>
                    <SelectItem value="America/Denver">Mountain Time</SelectItem>
                    <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Case Note AI */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Case Note AI
            </CardTitle>
            <CardDescription>AI assistance for case notes</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">AI-Assisted Case Notes</p>
                <p className="text-sm text-muted-foreground">Enable AI to help write case notes</p>
              </div>
              <Switch
                checked={settings.case_note_ai_enabled}
                onCheckedChange={(v) => setSettings({ ...settings, case_note_ai_enabled: v })}
              />
            </div>

            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Content Suggestions</p>
                <p className="text-sm text-muted-foreground">Suggest content based on case history</p>
              </div>
              <Switch
                checked={settings.case_note_ai_suggestions}
                onCheckedChange={(v) => setSettings({ ...settings, case_note_ai_suggestions: v })}
              />
            </div>

            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Grammar & Spelling Check</p>
                <p className="text-sm text-muted-foreground">Auto-check grammar and spelling</p>
              </div>
              <Switch
                checked={settings.case_note_ai_grammar_check}
                onCheckedChange={(v) => setSettings({ ...settings, case_note_ai_grammar_check: v })}
              />
            </div>
          </CardContent>
        </Card>

        {/* Knowledge Base */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Brain className="w-5 h-5" />
              Knowledge Base
            </CardTitle>
            <CardDescription>AI-powered knowledge resources</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Enable Knowledge Base</p>
                <p className="text-sm text-muted-foreground">Allow AI to reference uploaded documents</p>
              </div>
              <Switch
                checked={settings.knowledge_base_enabled}
                onCheckedChange={(v) => setSettings({ ...settings, knowledge_base_enabled: v })}
              />
            </div>
            
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">
                Knowledge Base allows you to upload policy documents, procedures, and resources that the AI can reference when generating content.
              </p>
              <Button variant="outline" size="sm" className="mt-3" disabled>
                Manage Documents (Coming Soon)
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AISettingsPage;
